import { Injectable } from '@angular/core';
import { DocumentReference, getDoc, setDoc } from '@angular/fire/firestore';
import { AuthService } from '@auth';
import { OrganizationSettings, AccountSettings } from '../../shared/models/account-settings';
import { FieldConfiguration } from '../../shared/models/field-configuration';
import { TransactionPreferencesDoc } from '../../shared/models/transaction-preferences';
import { FirestoreService } from './firestore.service';
import { PageName } from '@shared/enums/page-name';

@Injectable({
  providedIn: 'root'
})
export class AccountSettingsService {

  constructor(
    private authService: AuthService,
    private firestoreService: FirestoreService
  ) { }

  loadAccountSettings(): Promise<AccountSettings | undefined> {
    // Guard against an empty user id: firestore.doc() validates segment count synchronously and
    // would throw an "odd number of segments" error that bypasses downstream .catch() handlers.
    if (!this.authService.currentUserId) {
      return Promise.resolve(undefined);
    }
    return this.firestoreService.getDocValueAsync(this.accountSettingsDocument);
  }

  saveAccountSettings(data: AccountSettings): Promise<void> {
    const docRef = this.accountSettingsDocument;
    return this.firestoreService.settleWrite(() => setDoc(docRef, data, { merge: true }), docRef.path);
  }

  private get accountSettingsDocument(): DocumentReference<AccountSettings> {
    return this.firestoreService.doc(`accountSettings/${this.authService.currentUserId}`);
  }

  loadOrganizationSettings(userId: string): Promise<OrganizationSettings | undefined> {
    return this.firestoreService.getDocValueAsync(this.getOrganizationSettingsDocument(userId));
  }

  saveOrganizationSetting(userId: string, settings: OrganizationSettings): Promise<void> {
    const docRef = this.getOrganizationSettingsDocument(userId);
    return this.firestoreService.settleWrite(() => setDoc(docRef, settings, { merge: true }), docRef.path);
  }

  private getOrganizationSettingsDocument(userId: string): DocumentReference<OrganizationSettings> {
    return this.firestoreService.doc(`accountSettings/${userId}/organizationSettings/${this.authService.currentTenant}`);
  }

  /** `pageName` may also be a derived key (e.g. `${PageName}--section`) for pages with several field sets */
  async loadFieldsConfiguration(pageName: PageName | string): Promise<FieldConfiguration[] | undefined> {
    try {
      const docRef = this.getFieldsConfigurationDocument(pageName);
      const doc = await getDoc(docRef);
      if (doc.exists()) {
        return doc.data()?.configurations;
      }
      return undefined;
    } catch (error) {
      console.error('Error loading field configuration:', error);
      return undefined;
    }
  }

  async saveFieldsConfiguration(pageName: PageName | string, configurations: FieldConfiguration[]): Promise<void> {
    const docRef = this.getFieldsConfigurationDocument(pageName);
    return this.firestoreService.settleWrite(() => setDoc(docRef, { configurations }, { merge: true }), docRef.path);
  }

  /** Account-scoped transaction preferences, keyed by `TransactionDefinition.key` */
  async loadTransactionPreferences(definitionKey: string): Promise<TransactionPreferencesDoc | undefined> {
    if (!this.authService.currentUserId) {
      return undefined;
    }
    try {
      const doc = await getDoc(this.getTransactionPreferencesDocument(definitionKey));
      return doc.exists() ? doc.data() : undefined;
    } catch (error) {
      console.error('Error loading transaction preferences:', error);
      return undefined;
    }
  }

  /** Field-level merge so two tabs changing different preferences do not clobber each other */
  saveTransactionPreferences(definitionKey: string, patch: Partial<TransactionPreferencesDoc>): Promise<void> {
    if (!this.authService.currentUserId) {
      console.warn('Skipping transaction preferences write: no current user');
      return Promise.resolve();
    }
    const docRef = this.getTransactionPreferencesDocument(definitionKey);
    return this.firestoreService.settleWrite(() => setDoc(docRef, patch, { merge: true }), docRef.path);
  }

  private getTransactionPreferencesDocument(definitionKey: string): DocumentReference<TransactionPreferencesDoc> {
    return this.firestoreService.doc(`accountSettings/${this.authService.currentUserId}/transactionPreferences/${definitionKey}`);
  }

  private getFieldsConfigurationDocument(pageName: PageName | string): DocumentReference<{ configurations: FieldConfiguration[] }> {
    return this.firestoreService.doc(`accountSettings/${this.authService.currentUserId}/fieldsConfiguration/${pageName}`);
  }
}
